import { createClient } from '@supabase/supabase-js'
import logger from '../utils/logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("As variáveis de ambiente do Supabase não estão definidas corretamente. Certifique-se de que VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY estão definidas no arquivo .env")
}

// Remove token antigo se existir
if (typeof window !== 'undefined') {
    const oldToken = localStorage.getItem('alexender-auth-token');
    if (oldToken) {
        console.log('🧹 Removendo token antigo...');
        localStorage.removeItem('alexender-auth-token');
    }
}

// Cria o cliente Supabase com configurações padrão
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce'
    }
})

// Cache para resultados de verificação de pagamento
let paymentStatusCache = new Map();

const maskUUID = (uuid) => {
    if (!uuid) return 'undefined';
    const parts = uuid.split('-');
    if (parts.length !== 5) return '***';
    return `${parts[0].slice(0, 3)}***${parts[4].slice(-3)}`;
};

// Função para limpar cache de um usuário específico
export const clearPaymentStatusCache = (userId = null) => {
    if (userId) {
        paymentStatusCache.delete(userId);
    } else {
        paymentStatusCache.clear();
    }
};

// Função para verificar status do pagamento
export const checkPaymentStatus = async (userId) => {
    if (!userId) {
        logger.error('ID do usuário não fornecido para verificação de pagamento');
        return null;
    }

    const maskedUserId = maskUUID(userId);
    logger.payment(`Verificando status do pagamento para: ${maskedUserId}`);

    // Verifica cache primeiro
    if (paymentStatusCache.has(userId)) {
        const cachedStatus = paymentStatusCache.get(userId);
        logger.payment(`Usando resultado em cache: ${cachedStatus}`);
        return cachedStatus;
    }

    logger.payment('Buscando status no banco...');
    
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
        attempts++;
        logger.payment(`Tentativa ${attempts} de consulta ao banco...`);
        
        try {
            const { data, error } = await supabase
                .from('payments')
                .select('status')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(1);

            if (error) {
                logger.error('Erro ao consultar pagamento:', error.message);
                continue;
            }

            logger.payment('Dados recebidos:', data);

            if (data && data.length > 0) {
                const status = data[0].status;
                logger.payment(`Resultado da verificação de pagamento: ${status}`);
                paymentStatusCache.set(userId, status);
                return status;
            }
        } catch (error) {
            logger.error('Erro ao verificar pagamento:', error.message);
        }
    }

    return null;
};

// Função para validar e-mail
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// Função para validar senha
export const isValidPassword = (password) => {
    return password.length >= 8
}

// Função para validar WhatsApp
export const isValidWhatsApp = (whatsapp) => {
    const whatsappRegex = /^\d{10,11}$/
    return whatsappRegex.test(whatsapp.replace(/\D/g, ''))
}

// Função para verificar se o e-mail já existe
export const checkEmailExists = async (email) => {
    try {
        console.log("🔍 Verificando se o e-mail já existe:", email);

        const { data, error } = await supabase
            .from('auth.users')
            .select('email')
            .eq('email', email)
            .single();

        if (error) {
            console.error("❌ Erro ao verificar e-mail:", error);
            throw error;
        }

        const exists = data !== null;
        console.log(`✉️ Status do e-mail ${email}:`, exists ? 'Já cadastrado' : 'Disponível');
        
        return exists;
    } catch (error) {
        console.error("💥 Erro ao verificar e-mail:", error);
        throw error;
    }
};

// Função para cadastrar usuário
export const signUp = async ({ email, password, fullName, whatsapp }) => {
    try {
        // Log inicial com dados do cadastro (sem senha)
        console.log("🚀 Iniciando processo de cadastro:", {
            email,
            nome: fullName,
            whatsapp,
            timestamp: new Date().toISOString()
        });
        
        // Tenta realizar o cadastro
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: fullName,
                    whatsapp: whatsapp
                }
            }
        });

        // Log detalhado da resposta
        console.log("📡 Resposta do Supabase:", {
            sucesso: !error && !!data?.user,
            dados: data ? {
                usuario: data.user ? {
                    id: data.user.id,
                    email: data.user.email,
                    metadata: data.user.user_metadata
                } : null
            } : null,
            erro: error ? {
                mensagem: error.message,
                status: error.status,
                detalhes: error
            } : null,
            timestamp: new Date().toISOString()
        });

        // Se houver erro
        if (error) {
            console.error("❌ Erro do Supabase:", {
                mensagem: error.message,
                status: error.status,
                codigo: error.code,
                detalhes: error
            });

            const errorMessage = error.message?.toLowerCase() || '';

            // E-mail já cadastrado
            if (errorMessage.includes('already registered') || 
                errorMessage.includes('already exists') ||
                errorMessage.includes('email already')) {
                console.log("🔄 E-mail já cadastrado detectado:", email);
                return {
                    user: null,
                    error: new Error('Este e-mail já está cadastrado. Por favor, use outro e-mail ou faça login.'),
                    status: 'EMAIL_EXISTS'
                };
            }

            // Senha inválida
            if (errorMessage.includes('password')) {
                console.log("🔑 Erro de senha:", errorMessage);
                return {
                    user: null,
                    error: new Error('A senha deve ter pelo menos 8 caracteres.'),
                    status: 'UNKNOWN_ERROR'
                };
            }

            // E-mail inválido
            if (errorMessage.includes('email') && errorMessage.includes('valid')) {
                console.log("📧 E-mail inválido:", email);
                return {
                    user: null,
                    error: new Error('Por favor, insira um e-mail válido.'),
                    status: 'UNKNOWN_ERROR'
                };
            }

            // Erro genérico
            return {
                user: null,
                error: new Error(error.message || 'Erro ao criar conta. Por favor, tente novamente.'),
                status: 'UNKNOWN_ERROR'
            };
        }

        // Verifica se o usuário foi criado
        if (!data?.user) {
            console.error("⚠️ Resposta sem usuário:", {
                dados: data,
                timestamp: new Date().toISOString()
            });
            return {
                user: null,
                error: new Error('Não foi possível criar a conta. Por favor, tente novamente.'),
                status: 'UNKNOWN_ERROR'
            };
        }

        // Sucesso! Usuário criado
        console.log("✅ Cadastro realizado com sucesso:", {
            id: data.user.id,
            email: data.user.email,
            nome: data.user.user_metadata?.full_name,
            timestamp: new Date().toISOString()
        });

        return {
            user: data.user,
            error: null,
            status: 'SUCCESS'
        };

    } catch (error) {
        // Erro inesperado
        console.error("💥 Erro inesperado:", {
            mensagem: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
        });

        return {
            user: null,
            error: new Error('Erro inesperado. Por favor, tente novamente.'),
            status: 'UNEXPECTED_ERROR'
        };
    }
}

// Função para fazer login
export const signIn = async ({ email, password }) => {
    try {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        })

        // Se houver erro, verifica o tipo
        if (error) {
            // Credenciais inválidas
            if (error.status === 400 && error.message.includes('Invalid login credentials')) {
                return {
                    user: null,
                    error: new Error('E-mail ou senha incorretos.'),
                    status: 'INVALID_CREDENTIALS'
                }
            }

            // E-mail não confirmado
            if (error.status === 400 && error.message.includes('Email not confirmed')) {
                return {
                    user: null,
                    error: new Error('Por favor, confirme seu e-mail antes de fazer login.'),
                    status: 'EMAIL_NOT_CONFIRMED'
                }
            }

            // Outros erros
            return {
                user: null,
                error: new Error('Erro ao fazer login. Por favor, tente novamente.'),
                status: 'UNKNOWN_ERROR'
            }
        }

        // Verifica se o usuário foi retornado
        if (!data?.user) {
            return {
                user: null,
                error: new Error('Erro ao fazer login. Por favor, tente novamente.'),
                status: 'NO_USER_RETURNED'
            }
        }

        // Sucesso
        return {
            user: data.user,
            error: null,
            status: 'SUCCESS'
        }
    } catch (error) {
        console.error('Erro no login:', error)
        return {
            user: null,
            error: new Error('Erro inesperado. Por favor, tente novamente.'),
            status: 'UNEXPECTED_ERROR'
        }
    }
}

// Função para verificar se o e-mail foi confirmado
export const isEmailConfirmed = async () => {
    try {
        const { data: { user } } = await supabase.auth.getUser()
        return user?.email_confirmed_at ? true : false
    } catch (error) {
        console.error('Erro ao verificar confirmação de e-mail:', error)
        return false
    }
}

// Função para verificar se o usuário tem pagamento aprovado
export const checkUserPayment = async (userId) => {
    try {
        const maskedUserId = maskUUID(userId);
        logger.payment(`Verificando pagamento para: ${maskedUserId}`);

        const paymentStatus = await checkPaymentStatus(userId);
        return paymentStatus === 'approved';
    } catch (error) {
        console.error('💥 Erro inesperado ao verificar pagamento:', error);
        // Em caso de erro, vamos permitir o acesso para não bloquear o usuário
        return true;
    }
};
